/**
 * Shared Results Entry Component
 * Works for both Mexicano and Americano tournaments using adapters
 */

import React from 'react';
import { useLanguage } from '@/contexts/LanguageContext';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Medal, Trophy, Settings } from 'lucide-react';
import { TournamentAdapter } from '@/types/shared-tournament';
import { useTournamentResults } from '@/hooks/useTournamentResults';

interface SharedResultsEntryProps {
  adapter: TournamentAdapter;
  currentRound: number;
  totalRounds: number;
}

export const SharedResultsEntry: React.FC<SharedResultsEntryProps> = ({
  adapter,
  currentRound,
  totalRounds
}) => {
  const { t, language } = useLanguage();
  const config = adapter.getConfig();
  const matches = adapter.getMatches();
  
  const {
    results,
    totalPoints,
    setTotalPoints,
    updateScore,
    toggleSelectedTeam,
    handleSubmit
  } = useTournamentResults(adapter, currentRound);

  const showPointsSelector = config.allowPointsSelection && adapter.canModifyPoints(currentRound);

  return (
    <Card className="shadow-md">
      <CardHeader className="bg-accent/50 rounded-t-lg">
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
          <CardTitle className="text-lg sm:text-xl font-bold flex items-center gap-2">
            <Trophy className="text-primary h-5 w-5 flex-shrink-0" />
            <span className="truncate">
              {config.type === 'mexicano' 
                ? t("tournament.mexicano.enter_results")
                : (language === 'no' ? 'Registrer resultater' : 'Enter results')
              }
            </span>
          </CardTitle>
          
          {showPointsSelector && (
            <div className="flex items-center gap-2 flex-shrink-0">
              <Settings className="text-primary h-4 w-4" />
              <Select 
                value={totalPoints.toString()} 
                onValueChange={(value) => setTotalPoints(parseInt(value))}
              >
                <SelectTrigger className="w-28 bg-accent/30">
                  <SelectValue>
                    {totalPoints} {config.type === 'mexicano' ? t("tournament.mexicano.points") : (language === 'no' ? 'poeng' : 'points')}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent className="bg-background border-primary/20">
                  {config.pointsOptions.map(points => (
                    <SelectItem key={points} value={points.toString()} className="hover:bg-accent/20">
                      {points} {config.type === 'mexicano' ? t("tournament.mexicano.points") : (language === 'no' ? 'poeng' : 'points')}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}
        </div>
      </CardHeader>
      
      <CardContent className="space-y-6 py-6">
        {matches.map((match, courtIndex) => (
          <Card key={courtIndex} className="overflow-hidden border-primary/10 mb-4">
            <div className="bg-primary/5 p-3">
              <h3 className="font-bold text-primary text-sm sm:text-base">
                {language === 'no' ? `Bane ${courtIndex + 1}` : `Court ${courtIndex + 1}`}
              </h3>
            </div>
            
            <div className="p-4">
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-4">
                <div 
                  className={`p-3 rounded-md border cursor-pointer ${
                    results[courtIndex]?.selectedTeam === "team1" 
                      ? 'bg-primary/20 border-primary' 
                      : 'bg-primary/10 border-primary/20'
                  }`}
                  onClick={() => toggleSelectedTeam(courtIndex)}
                >
                  <div className="flex items-center justify-between">
                    <p className="text-sm sm:text-base font-medium truncate pr-2">
                      {match.team1.player1.name} & {match.team1.player2.name}
                    </p>
                    <span className="text-lg font-bold flex-shrink-0">
                      {results[courtIndex]?.score[0] || 0}
                    </span>
                  </div>
                </div>
                
                <div 
                  className={`p-3 rounded-md border cursor-pointer ${
                    results[courtIndex]?.selectedTeam === "team2" 
                      ? 'bg-secondary/20 border-secondary' 
                      : 'bg-secondary/10 border-secondary/20'
                  }`}
                  onClick={() => toggleSelectedTeam(courtIndex)}
                >
                  <div className="flex items-center justify-between">
                    <p className="text-sm sm:text-base font-medium truncate pr-2">
                      {match.team2.player1.name} & {match.team2.player2.name}
                    </p>
                    <span className="text-lg font-bold flex-shrink-0">
                      {results[courtIndex]?.score[1] || 0}
                    </span>
                  </div>
                </div>
              </div>

              {/* Score buttons */}
              <div className="mt-4">
                <p className="text-sm sm:text-base text-center mb-2">
                  {results[courtIndex]?.selectedTeam === "team1" 
                    ? (config.type === 'mexicano' 
                        ? t("tournament.mexicano.select_left_team_score")
                        : (language === 'no' ? 'Velg poeng for venstre lag:' : 'Select points for left team:')
                      )
                    : (language === 'no' ? 'Velg poeng for høyre lag:' : 'Select points for right team:')
                  }
                </p>
                <div className="flex flex-wrap gap-1 justify-center">
                  {Array.from({ length: totalPoints + 1 }, (_, i) => {
                    const selectedTeamScore = results[courtIndex]?.selectedTeam === "team1" 
                      ? results[courtIndex]?.score[0] 
                      : results[courtIndex]?.score[1];
                    return (
                      <Button
                        key={i}
                        size="sm"
                        variant={selectedTeamScore === i ? "default" : "outline"}
                        className={`w-6 h-6 sm:w-8 sm:h-8 p-0 text-xs ${
                          selectedTeamScore === i ? 'ring-2 ring-primary/50 ring-offset-1' : ''
                        }`}
                        onClick={() => updateScore(courtIndex, i)}
                      >
                        {i}
                      </Button>
                    );
                  })}
                </div>
              </div>
              
              <div className="text-center mt-4">
                {results[courtIndex] && results[courtIndex].score[0] !== results[courtIndex].score[1] ? (
                  <div className="flex items-center justify-center gap-2 text-green-600 font-medium">
                    <Medal className="h-4 w-4 flex-shrink-0" />
                    <span className="text-sm sm:text-base truncate">
                      {results[courtIndex].score[0] > results[courtIndex].score[1] 
                        ? `${match.team1.player1.name} & ${match.team1.player2.name}` 
                        : `${match.team2.player1.name} & ${match.team2.player2.name}`
                      }
                      <span className="font-bold ml-1">
                        ({Math.max(...results[courtIndex].score)}-{Math.min(...results[courtIndex].score)})
                      </span>
                    </span>
                  </div>
                ) : (
                  <div className="flex items-center justify-center gap-2 text-blue-600 font-medium">
                    <Medal className="h-4 w-4 flex-shrink-0" />
                    <span className="text-sm sm:text-base">
                      {config.type === 'mexicano' 
                        ? t("tournament.mexicano.tie")
                        : (language === 'no' ? 'Uavgjort' : 'Tie')
                      } ({results[courtIndex]?.score[0] || 0}-{results[courtIndex]?.score[1] || 0})
                    </span>
                  </div>
                )}
              </div>
            </div>
          </Card>
        ))}
      </CardContent>
      
      <CardFooter className="bg-accent/30 rounded-b-lg pt-4">
        <Button onClick={handleSubmit} className="w-full bg-success hover:bg-success/90 text-white font-bold">
          {currentRound >= totalRounds 
            ? (language === 'no' ? 'Fullfør turnering' : 'Finish tournament')
            : (language === 'no' ? 'Gå til neste runde' : 'Go to next round')
          }
        </Button>
      </CardFooter>
    </Card>
  );
};
